import React from "react";
import "../styles/videojs-theme.css";
import axios from "axios";
import config from "../config";
import socketIOClient from "socket.io-client";
import {
  loadCmsPlayerStack,
  ensureMasterPlaylist,
  probeM3u8Variants,
} from "../utils/loadCmsPlayerStack";

const socket = socketIOClient("https://socket-io.i-iptv.com");

const PLAYER_ID = "studio-emplayer";

const setPlayerOverlay = (player, html) => {
  if (!player || player.isDisposed()) return;
  const playerEl = player.el();
  if (!playerEl) return;

  let overlay = playerEl.querySelector(".vjs-live-overlay");
  if (!overlay) {
    overlay = document.createElement("div");
    overlay.className = "vjs-live-overlay";
    overlay.style.cssText =
      "position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none;z-index:2;";
    playerEl.appendChild(overlay);
  }
  overlay.innerHTML = html;
};

const buildLiveOverlay = (viewerCount, showViewers) => {
  const liveBadge =
    "<span style='display:inline-block;background:#e11d48;color:#fff;padding:3px 8px;border-radius:3px;font-weight:700;font-size:12px;letter-spacing:0.06em;'>LIVE</span>";
  const viewerBadge = showViewers
    ? ` <span style='display:inline-block;background:rgba(0,0,0,0.55);color:#fff;margin-left:6px;padding:3px 8px;border-radius:3px;font-size:12px;font-weight:600;'><i class='fas fa-eye'></i> ${viewerCount}</span>`
    : "";
  return `<div style='position:absolute;left:12px;top:12px;'>${liveBadge}${viewerBadge}</div>`;
};

/** Show eye/CCU badge only when studio Viewer display is enabled. */
const isViewersEnabled = (userViews) => {
  if (userViews === true || userViews === 1) return true;
  const s = String(userViews ?? "")
    .trim()
    .toLowerCase();
  if (
    !s ||
    s === "false" ||
    s === "0" ||
    s === "disable" ||
    s === "disabled" ||
    s === "off"
  ) {
    return false;
  }
  return s === "true" || s === "1" || s === "enable" || s === "enabled" || s === "on";
};

const parseStreamName = (streamUrl) => {
  try {
    const smilName = String(streamUrl || "").split("/");
    const streamName = (smilName[4] || "").split(".");
    const FinalName = (streamName[0] || "").split(":");
    return FinalName.length === 1 ? FinalName[0] : FinalName[1];
  } catch (e) {
    return "";
  }
};

const levelSummary = (ql) => {
  if (!ql || !ql.length) return "none";
  const heights = [];
  for (let i = 0; i < ql.length; i++) {
    heights.push(ql[i].height || ql[i].bitrate || "?");
  }
  return `${ql.length}[${heights.join(",")}]`;
};

const channelPayload = (channelId) => ({
  id: channelId,
  userid: localStorage.StudioUserID,
});

/**
 * Prefer detail.stream_url (SMIL ABR). Fall back to preview if needed.
 */
const fetchPlaybackUrl = async (channelId) => {
  try {
    const detailRes = await axios.post(
      config.host + "/api/v1/live/stream/detail",
      channelPayload(channelId)
    );
    const result = detailRes.data?.result || {};
    const detailUrl = result.stream_url;
    if (detailUrl) {
      return { rawUrl: detailUrl, detail: result, source: "detail" };
    }
  } catch (e) {
    console.warn("[studio-player] detail failed, trying preview", e);
  }

  const previewRes = await axios.post(
    config.host + "/api/v1/live/stream/preview",
    channelPayload(channelId)
  );
  return {
    rawUrl: previewRes.data?.stream_url || "",
    detail: null,
    source: "preview",
  };
};

const isRadioService = (detail) =>
  String(detail?.service || detail?.type || "")
    .toLowerCase()
    .trim() === "radio";

export default class VideoPlayer extends React.Component {
  player = null;
  streamName = "";
  socketHandler = null;
  reloadTimer = null;
  lastReloadAt = 0;
  mounted = false;
  mountEl = null;
  streamUrl = "";
  isRadio = false;

  state = {
    streamName: "",
  };

  componentDidMount() {
    this.mounted = true;
    this.initPlayer();
  }

  setDebug = (info) => {
    window.__studioPlayerDebug = info;
    console.log("[studio-player]", info);
  };

  dumpDebug = (tag) => {
    const player = this.player;
    if (!player || player.isDisposed()) return;
    try {
      const ql =
        player.qualityLevels && typeof player.qualityLevels === "function"
          ? player.qualityLevels()
          : null;
      const wrap =
        player.el() && player.el().querySelector(".vjs-quality-menu-wrapper");
      const btn =
        player.el() && player.el().querySelector(".vjs-quality-menu-button");
      const src = player.currentSrc() || this.streamUrl || "";
      this.setDebug({
        tag,
        vjs: window.__cmsVideojs && window.__cmsVideojs.VERSION,
        hasPlugin: !!(
          window.__cmsVideojs &&
          window.__cmsVideojs.getPlugin &&
          window.__cmsVideojs.getPlugin("qualityMenu")
        ),
        levels: ql ? ql.length : null,
        levelSummary: levelSummary(ql),
        m3u8Variants: this._m3u8Variants,
        hasWrap: !!wrap,
        wrapHidden: wrap ? wrap.classList.contains("vjs-hidden") : null,
        hasBtn: !!btn,
        btnHidden: btn ? btn.classList.contains("vjs-hidden") : null,
        src,
        srcTail: src ? src.slice(-70) : "",
      });
    } catch (e) {
      console.warn("[studio-player] dump failed", e);
    }
  };

  /**
   * Play API stream_url. ensureMasterPlaylist keeps ABR if a non-SMIL URL slips through.
   */
  playMaster = async (rawUrl) => {
    if (!this.player || this.player.isDisposed() || !rawUrl) return;

    const masterUrl = ensureMasterPlaylist(rawUrl);
    let streamUrl = masterUrl;
    let variants = null;

    if (masterUrl !== rawUrl) {
      console.log("[studio-player] normalized to SMIL master", {
        rawUrl,
        masterUrl,
      });
    }

    try {
      variants = await probeM3u8Variants(masterUrl);
      console.log("[studio-player] m3u8 variants", variants, masterUrl);
      if (variants === 0 && masterUrl !== rawUrl) {
        console.warn(
          "[studio-player] SMIL has no variants — falling back to API URL"
        );
        streamUrl = rawUrl;
        variants = await probeM3u8Variants(rawUrl).catch(() => 0);
      }
    } catch (e) {
      console.warn("[studio-player] SMIL probe failed", e);
      if (masterUrl !== rawUrl) {
        console.warn("[studio-player] falling back to API URL", rawUrl);
        streamUrl = rawUrl;
        try {
          variants = await probeM3u8Variants(rawUrl);
        } catch (e2) {
          variants = null;
        }
      }
    }

    this._m3u8Variants = variants;
    this.streamUrl = streamUrl;

    if (!this.player || this.player.isDisposed()) return;

    if (typeof this.player.qualityLevels === "function") {
      this.player.qualityLevels();
    }

    this.player.src({
      type: "application/x-mpegURL",
      src: streamUrl,
    });

    if (this.isRadio) {
      const wrap = this.player.el()?.querySelector(".vjs-quality-menu-wrapper");
      if (wrap) wrap.classList.add("vjs-hidden");

      const ql =
        typeof this.player.qualityLevels === "function"
          ? this.player.qualityLevels()
          : null;
      if (ql) {
        for (let i = 0; i < ql.length; i++) {
          ql[i].enabled = ql[i].height === 1080;
        }
      }
    } else if (typeof this.player.qualityMenu === "function") {
      this.player.qualityMenu({
        useResolutionLabels: true,
        defaultResolution: "Auto",
      });
    }

    this.player.muted(false);
    this.player.play().catch(() => {});
  };

  applyDetailOverlay = (result, channelId, streamName) => {
    const detail = result || {};
    this.isRadio = isRadioService(detail);

    if (this.isRadio && this.player && !this.player.isDisposed()) {
      const wrap = this.player.el()?.querySelector(".vjs-quality-menu-wrapper");
      if (wrap) wrap.classList.add("vjs-hidden");

      const ql =
        typeof this.player.qualityLevels === "function"
          ? this.player.qualityLevels()
          : null;
      if (ql) {
        for (let i = 0; i < ql.length; i++) {
          ql[i].enabled = ql[i].height === 1080;
        }
      }
    }

    const ccuCount = Number(detail.ccu);
    const viewerCount = Number.isFinite(ccuCount) ? ccuCount : 0;
    const showViewers = isViewersEnabled(detail.user_views);
    setPlayerOverlay(this.player, buildLiveOverlay(viewerCount, showViewers));
    this.bindSocket(streamName, channelId, showViewers);
  };

  loadChannel = async (channelId) => {
    if (!this.player || this.player.isDisposed() || !channelId) return;

    try {
      const { rawUrl, detail, source } = await fetchPlaybackUrl(channelId);
      if (!rawUrl || !this.player || this.player.isDisposed()) return;

      console.log("[studio-player] playback source", source, rawUrl);
      this.isRadio = isRadioService(detail);
      this.playMaster(rawUrl);

      const resolvedStreamName = parseStreamName(ensureMasterPlaylist(rawUrl));
      this.streamName = resolvedStreamName;
      this.setState({ streamName: resolvedStreamName });

      setTimeout(() => this.dumpDebug("t+2s"), 2000);
      setTimeout(() => this.dumpDebug("t+5s"), 5000);

      if (detail) {
        this.applyDetailOverlay(detail, channelId, resolvedStreamName);
        return;
      }

      // Preview fallback path — still fetch detail for CCU / viewers
      try {
        const detailRes = await axios.post(
          config.host + "/api/v1/live/stream/detail",
          channelPayload(channelId)
        );
        this.applyDetailOverlay(
          detailRes.data?.result || {},
          channelId,
          resolvedStreamName
        );
      } catch (e) {
        setPlayerOverlay(this.player, buildLiveOverlay(0, false));
        this.bindSocket(resolvedStreamName, channelId, false);
      }
    } catch (err) {
      console.error("[studio-player] load channel failed", err);
    }
  };

  initPlayer = async () => {
    if (!this.mountEl) return;

    let videojs;
    try {
      videojs = await loadCmsPlayerStack();
    } catch (e) {
      console.error("Failed to load CMS Video.js stack", e);
      return;
    }
    if (!this.mounted || !this.mountEl) return;

    window.videojs = videojs;

    try {
      const existing = videojs.getPlayer && videojs.getPlayer(PLAYER_ID);
      if (existing && !existing.isDisposed()) existing.dispose();
    } catch (e) {}

    this.mountEl.innerHTML = `<video id="${PLAYER_ID}" class="video-js vjs-big-play-centered vjs-fill" controls playsinline></video>`;

    this.player = videojs(PLAYER_ID, {
      liveui: true,
      autoplay: true,
      muted: false,
      controls: true,
      fill: true,
      fluid: false,
      html5: {
        vhs: { overrideNative: true },
        nativeAudioTracks: false,
        nativeVideoTracks: false,
      },
      controlBar: {
        volumePanel: { inline: true },
        pictureInPictureToggle: true,
      },
      plugins: {
        qualityMenu: {
          useResolutionLabels: true,
          defaultResolution: "Auto",
        },
      },
    });

    this.player.on("loadedmetadata", () => this.dumpDebug("loadedmetadata"));
    this.player.on("loadeddata", () => this.dumpDebug("loadeddata"));

    const channelId = window.location.pathname.split("/")[2];

    this.player.ready(() => {
      if (!this.player || this.player.isDisposed()) return;
      this.loadChannel(channelId);
    });
  };

  bindSocket = (streamName, channelId, showViewers) => {
    if (!streamName) return;

    if (this.socketHandler && this.streamName) {
      socket.off(this.streamName, this.socketHandler);
    }

    this.socketHandler = (entry) => {
      if (!this.player || this.player.isDisposed()) return;

      if (entry && entry.reload === "true") {
        this.softReload(channelId);
        return;
      }

      setPlayerOverlay(
        this.player,
        buildLiveOverlay(
          showViewers
            ? Number(entry.ccu) || Number(entry.view) || Number(entry.views) || 0
            : 0,
          showViewers
        )
      );
    };

    this.streamName = streamName;
    socket.on(streamName, this.socketHandler);
  };

  softReload = (channelId) => {
    const now = Date.now();
    if (now - this.lastReloadAt < 5000) return;
    this.lastReloadAt = now;

    if (this.reloadTimer) clearTimeout(this.reloadTimer);

    this.reloadTimer = setTimeout(async () => {
      try {
        const { rawUrl } = await fetchPlaybackUrl(channelId);
        if (!this.player || this.player.isDisposed() || !rawUrl) return;

        const master = ensureMasterPlaylist(rawUrl);
        const currentUrl = this.player.currentSrc();
        if (currentUrl && (master === currentUrl || rawUrl === currentUrl)) {
          try {
            this.player.liveTracker && this.player.liveTracker.seekToLiveEdge();
          } catch (e) {}
          return;
        }

        this.playMaster(rawUrl);
      } catch (e) {}
    }, 300);
  };

  componentWillUnmount() {
    this.mounted = false;
    if (this.reloadTimer) clearTimeout(this.reloadTimer);
    if (this.socketHandler && this.streamName) {
      socket.off(this.streamName, this.socketHandler);
    }
    if (this.player) {
      try {
        this.player.dispose();
      } catch (e) {}
      this.player = null;
    }
    if (this.mountEl) this.mountEl.innerHTML = "";
  }

  render() {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          ref={(node) => {
            this.mountEl = node;
          }}
          className="studio-vjs-host"
          style={{ width: "100%", height: "100%" }}
        />
      </div>
    );
  }
}
